export function MarketInsights() {
  const marketFactors = [
    {
      icon: 'ri-line-chart-line',
      title: 'Growing Demand',
      description: 'Langley is experiencing steady population growth and economic development, driving increased demand for short-term accommodations from business travelers, families visiting residents, and tourists exploring the Fraser Valley.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Competitive Pricing',
      description: 'Average nightly rates in Langley range from $120-$180, offering attractive returns while remaining more affordable than Vancouver. Lower property acquisition costs improve overall investment ROI.'
    },
    {
      icon: 'ri-map-pin-2-line',
      title: 'Strategic Location',
      description: 'Positioned between Vancouver and the Fraser Valley, Langley attracts guests seeking proximity to urban amenities while enjoying suburban comfort and access to outdoor recreation.'
    },
    {
      icon: 'ri-building-line',
      title: 'Diverse Property Types',
      description: 'From urban condos in City of Langley to spacious rural homes in the Township, diverse property types cater to different guest preferences and investment strategies.'
    },
    {
      icon: 'ri-calendar-event-line',
      title: 'Year-Round Potential',
      description: 'Consistent demand throughout the year from business travelers, event attendees, and visitors to local attractions like Fort Langley and Campbell Valley Regional Park.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Lower Competition',
      description: 'Compared to Vancouver and Whistler, Langley has fewer short-term rental operators, creating opportunities for well-managed properties to capture market share and maintain higher occupancy rates.'
    }
  ];

  const neighborhoods = [
    {
      name: 'Willoughby',
      type: 'Township - Suburban',
      avgRate: '$140-$170/night',
      occupancy: '65-75%',
      image: 'https://public.readdy.ai/ai/img_res/668c7eee7108569e5a648f4c3a0baf64.jpg',
      highlights: [
        'Newer developments',
        'Family-friendly amenities',
        'Good school access',
        'Transit connections'
      ]
    },
    {
      name: 'Walnut Grove',
      type: 'Township - Established',
      avgRate: '$150-$180/night',
      occupancy: '70-80%',
      image: 'https://readdy.ai/api/search-image?query=Walnut%20Grove%20Langley%20established%20suburban%20community%20with%20mature%20trees%20and%20well-maintained%20homes%2C%20mix%20of%20single-family%20houses%20and%20townhomes%2C%20peaceful%20residential%20streets%20with%20parks%20and%20shopping%20nearby%2C%20professional%20neighborhood%20photography&width=600&height=400&seq=langley-walnut-grove-001&orientation=landscape',
      highlights: [
        'Established community',
        'Higher-end properties',
        'Shopping and dining',
        'Strong rental demand'
      ]
    },
    {
      name: 'Fort Langley',
      type: 'Township - Historic',
      avgRate: '$160-$200/night',
      occupancy: '75-85%',
      image: 'https://readdy.ai/api/search-image?query=Fort%20Langley%20historic%20village%20with%20heritage%20buildings%20and%20charming%20main%20street%2C%20quaint%20shops%20and%20restaurants%20along%20tree-lined%20streets%2C%20Fraser%20River%20waterfront%20views%2C%20professional%20tourism%20photography%20showing%20historic%20character%20and%20natural%20beauty&width=600&height=400&seq=langley-fort-langley-001&orientation=landscape',
      highlights: [
        'Tourist destination',
        'Historic charm',
        'Waterfront access',
        'Premium pricing potential'
      ]
    },
    {
      name: 'Downtown Langley City',
      type: 'City - Urban Core',
      avgRate: '$120-$150/night',
      occupancy: '60-70%',
      image: 'https://readdy.ai/api/search-image?query=Downtown%20Langley%20City%20urban%20core%20with%20modern%20mixed-use%20buildings%20and%20commercial%20development%2C%20walkable%20streets%20with%20shops%20and%20restaurants%2C%20compact%20city%20center%20with%20residential%20towers%2C%20professional%20urban%20photography%20showing%20city%20amenities&width=600&height=400&seq=langley-downtown-001&orientation=landscape',
      highlights: [
        'Urban convenience',
        'Transit hub access',
        'Business travelers',
        'Condo opportunities'
      ]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Langley Market Insights
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Understanding Langley's rental market dynamics helps maximize your property's potential and investment returns.
          </p>
        </div>

        {/* Market Factors */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {marketFactors.map((factor, index) => (
            <div key={index} className="bg-[#F0F0F0] rounded-2xl p-6 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#882629] rounded-xl flex items-center justify-center mb-4">
                <i className={`${factor.icon} text-white text-2xl`}></i>
              </div>
              <h3 className="font-['Fraunces'] text-lg font-bold text-[#505A5A] mb-3">
                {factor.title}
              </h3>
              <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                {factor.description}
              </p>
            </div>
          ))}
        </div>

        {/* Neighborhood Analysis */}
        <div>
          <h3 className="font-['Fraunces'] text-3xl font-bold text-[#505A5A] mb-12 text-center">
            Top Neighborhoods for Short-Term Rentals
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {neighborhoods.map((neighborhood, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={neighborhood.image}
                    alt={neighborhood.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="font-['Fraunces'] text-2xl font-bold text-white mb-1">
                      {neighborhood.name}
                    </h4>
                    <p className="text-white/90 text-sm">{neighborhood.type}</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-[#505A5A]/10">
                    <div>
                      <p className="text-xs text-[#505A5A]/60 mb-1">Avg. Nightly Rate</p>
                      <p className="font-semibold text-[#882629]">{neighborhood.avgRate}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#505A5A]/60 mb-1">Occupancy Rate</p>
                      <p className="font-semibold text-[#882629]">{neighborhood.occupancy}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-[#505A5A] mb-3">Key Highlights:</p>
                    <ul className="space-y-2">
                      {neighborhood.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <i className="ri-star-fill text-[#882629] text-sm w-4 h-4 flex items-center justify-center"></i>
                          <span className="text-sm text-[#505A5A]/80">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Potential */}
        <div className="mt-16 bg-[#F0F0F0] rounded-2xl p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-[#882629] rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-funds-line text-white text-3xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-4">
                Strong Investment Potential
              </h3>
              <p className="text-[#505A5A]/70 leading-relaxed mb-4">
                Langley offers compelling investment opportunities for short-term rental operators. With lower property acquisition costs compared to Vancouver, more flexible regulations, and growing demand from both business and leisure travelers, investors can achieve attractive returns while building long-term equity.
              </p>
              <p className="text-[#505A5A]/70 leading-relaxed">
                <strong>Average annual revenue potential:</strong> Well-managed properties in prime neighborhoods can generate $25,000-$45,000 annually, with Fort Langley and Walnut Grove commanding premium rates. Combined with property appreciation in this growing market, Langley presents a balanced risk-reward profile for rental property investors.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
